import * as React from 'react'
import moment from 'moment'
import styled from 'styled-components'
import I18n, { tJa } from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { EditTable } from '../../../organisms'
import { IColumn } from 'components/organisms/EditTable'
import { IOtaReservationFilterOptions } from '../../../organisms/OtaReservations/OtaReservationFilter'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import OtaReservationSearch from 'components/organisms/OtaReservations/OtaReservationSearch'
import OtaReservationDatePicker from 'components/organisms/OtaReservations/OtaReservationDatePicker'
import SvgIcon from 'components/atoms/SvgIcon'
import { Button } from 'components/atoms'

import * as constants from '../../../../static/constants'
import AgentLayoutIndex from '../_layouts'

declare var window: interfaces.IWindow

const DATE_FORMAT = 'YYYY-MM-DD'

const getInitialTourDate = () => {
  const queryDate = new URLSearchParams(window.location.search).get('tour_date')
  const parsedDate = moment(queryDate || '', DATE_FORMAT, true)

  return parsedDate.isValid() ? parsedDate.format(DATE_FORMAT) : moment().format(DATE_FORMAT)
}

const getStatusLabel = record => {
  if (record.booking_status_id === 'canceled') {
    return 'Cancel'
  }

  if (record.booking_status_id === 'confirmed') {
    return 'Confirm'
  }

  return ''
}

const getReservationId = record =>
  record.seller === 'jwt' ? record.jwt_reservation_id : record.seller_booking_id

interface IProps {
  user: {
    users: IJsonResponse
    meta: interfaces.IPager
  }
}

const CHECKIN_STATUS_FLAGS = {
  not_yet: { className: 'notCheckedIn', label: '—' },
  checked_in: { className: 'isCheckedIn', label: 'OK' },
  no_show: { className: 'isNoShow', label: 'No Show' },
}

// sortKey は OtaReservation::ORDER_BY_EXPRESSIONS のキーと一致させる
const COLUMNS: Array<IColumn & { sortKey?: string }> = [
  {
    name: I18n.t('generic.id'),
    field: 'id',
    className: 'noWrap',
    required: false,
    readOnly: true,
    link: record => `/agent/ota_reservations/${record.id}`,
  },
  {
    name: I18n.t('generic.opcalendar_info'),
    renderElement: record => {
      return (
        <>
          {record.tour_product_id}
          <br />
          {record.jwt_post_id ? (
            <a
              style={{ wordBreak: 'break-all' }}
              href={`/posts/${record.jwt_post_id}`}
              target="_blank"
            >
              {record.tour_name}
            </a>
          ) : (
            record.tour_name
          )}
        </>
      )
    },
    field: 'tour_product_id',
    sortKey: 'tour_product_id',
    required: false,
  },
  {
    name: I18n.t('generic.post_id'),
    renderElement: record => {
      if (!record.jwt_tour_id) {
        return <>{'-'}</>
      }

      return (
        <a
          href={`/agent/tours/weekly?start=${record.tour_date}&tour_id=${record.jwt_tour_id}`}
          target="_blank"
        >
          {record.jwt_tour_id}
        </a>
      )
    },
    className: 'noWrap',
    field: 'jwt_tour_id',
    sortKey: 'jwt_tour_id',
    required: false,
  },
  {
    name: I18n.t('generic.tour_date'),
    renderElement: record => {
      if (!record.tour_start_at) {
        return <></>
      }
      const startTime = moment.utc(record.tour_start_at).utcOffset(9)
      const dayOfWeek = moment(record.tour_date, DATE_FORMAT)
        .locale('ja')
        .format('ddd')
      return (
        <div className="tourDate">
          {record.tour_date} ({dayOfWeek}) {startTime.format('HH:mm')}
        </div>
      )
    },
    field: 'tour_date',
    sortKey: 'tour_date',
    className: 'tourDateColumn',
    required: false,
  },
  {
    name: I18n.t('generic.status'),
    renderElement: record => {
      return <>{getStatusLabel(record)}</>
    },
    field: 'booking_status_id',
    sortKey: 'booking_status_id',
    className: 'noWrap',
    required: false,
  },
  {
    name: '予約ID',
    renderElement: record => <>{getReservationId(record) || '-'}</>,
    field: 'seller_booking_id',
    className: 'noWrap',
    required: false,
  },
  {
    name: I18n.t('generic.guest_info'),
    renderElement: record => {
      return (
        <>
          {record.customer_first_name || ''} {record.customer_last_name || ''}
          <br />
          {record.num_of_adults} / {record.num_of_children} / {record.num_of_infants}
        </>
      )
    },
    field: 'customer_name',
    sortKey: 'num_of_people',
    required: false,
  },
  {
    name: 'Check-in',
    renderElement: record => (
      <span
        className={`statusFlag checkInFlag ${
          CHECKIN_STATUS_FLAGS[record.checkin_status].className
        }`}
        title={tJa(`enums.ota_reservation.checkin_status.${record.checkin_status}`)}
      >
        {CHECKIN_STATUS_FLAGS[record.checkin_status].label}
      </span>
    ),
    field: 'checkin_status',
    required: false,
  },
  {
    name: '繋ぎ状況',
    renderElement: record => {
      if (!record.is_private_tour) {
        return <>{'-'}</>
      }

      const isConnected = record.connection?.status === 'connected'
      const unDoneTasks = record.un_done_tasks || []

      if (!isConnected && unDoneTasks.length > 0) {
        return (
          <div className="connectionTodos">
            {unDoneTasks.map(({ id, label }) => (
              <span key={id} title={label}>
                {label}
              </span>
            ))}
          </div>
        )
      }

      return (
        <div
          className={`statusFlag connectionFlag ${isConnected ? 'isConnected' : 'notConnected'}`}
          title={isConnected ? 'つなぎ済' : '未つなぎ'}
        >
          <SvgIcon
            name={isConnected ? 'connection_linked' : 'connection_unlinked'}
            className="icon"
            ariaHidden={true}
            role="presentation"
          />
        </div>
      )
    },
    field: 'connection',
    sortKey: 'connection_status',
    required: false,
  },
  {
    name: 'BOKUN',
    renderElement: record => {
      const isBokun = record.channel_manager === 'bokun'
      if (!isBokun || !record.channel_manager_booking_id) {
        return <>{'-'}</>
      }

      return (
        <a
          href={`https://japanwondertravel.bokun.io/sales/${record.channel_manager_booking_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Bokun: {record.channel_manager_booking_id}
        </a>
      )
    },
    field: 'channel_manager_booking_id',
    required: false,
  },
]

const AgentOtaReservationIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('OTA Reservations')
  const [otaReservations, setOtaReservations] = React.useState([])
  const { data: user } = agentService.getDataFromJson(props.user)
  const defaultFilterOptions = React.useMemo<IOtaReservationFilterOptions>(
    () => ({ tour_date: getInitialTourDate() }),
    []
  )
  const [filterOptions, setFilterOptions] = React.useState(defaultFilterOptions)
  const [pagination, setPagination] = React.useState()
  const [isLoading, setIsLoading] = React.useState(true)
  // null は未選択(API 既定の id 降順)
  const [sortOrder, setSortOrder] = React.useState<{
    column: string
    direction: 'asc' | 'desc'
  } | null>(null)
  // 連続操作時に古いレスポンスで上書きしないための世代カウンタ
  const latestRequestIdRef = React.useRef(0)

  const searchOtaReservations = React.useCallback(
    async _page => {
      const requestId = ++latestRequestIdRef.current
      try {
        setIsLoading(true)
        window.scrollTo({ top: 0 })
        const {
          ota_reservations: fetchedOtaReservations,
          pagination: meta,
        } = await agentService.getOtaReservations(
          { ...filterOptions, sort: sortOrder?.column, direction: sortOrder?.direction },
          _page
        )
        if (requestId !== latestRequestIdRef.current) {
          return
        }
        setOtaReservations(fetchedOtaReservations)
        setPagination(meta)
      } catch (e) {
        console.error(e)
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setIsLoading(false)
        }
      }
    },
    // sortOrder を依存に含めることで OtaReservationSearch 側の effect が発火し、1ページ目から再取得される
    [filterOptions, sortOrder]
  )

  const columns = React.useMemo(
    () =>
      COLUMNS.map(column => {
        if (!column.sortKey) {
          return column
        }
        const isActive = sortOrder?.column === column.sortKey
        const isAsc = isActive && sortOrder.direction === 'asc'
        return {
          ...column,
          renderHeader: () => (
            <span
              className={`sortableHeader${isActive ? ' isActive' : ''}`}
              role="button"
              aria-label={`${column.name} で並び替え`}
              onClick={() =>
                setSortOrder({
                  column: column.sortKey,
                  direction: isActive && !isAsc ? 'asc' : 'desc',
                })
              }
            >
              {column.name}
              <SvgIcon
                name={isAsc ? 'sort_asc' : 'sort_desc'}
                className="sortIcon"
                ariaHidden={true}
                role="presentation"
              />
            </span>
          ),
        }
      }),
    [sortOrder]
  )

  const handleTourDateChange = React.useCallback((tourDate: string) => {
    setFilterOptions(currentOptions => ({
      ...currentOptions,
      tour_date: tourDate || undefined,
    }))

    const currentUrlParams = new URLSearchParams(window.location.search)
    if (tourDate) {
      currentUrlParams.set('tour_date', tourDate)
    } else {
      currentUrlParams.delete('tour_date')
    }
    currentUrlParams.delete('page')
    const queryString = currentUrlParams.toString()
    window.history.replaceState(
      {},
      '',
      queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
    )
  }, [])

  return (
    <SidebarLayout user={user} maxContent={true}>
      <Wrapper>
        <style type="text/css">{`
          @container filter-container (width < 640px) {
            .filterContainer {
              width: 100%;
              max-width: 240px;
            }
          }
        `}</style>
        <AgentLayoutIndex
          hideAgentNav={true}
          title={
            <Heading>
              <div className="headingInner">
                <div className="headingTitleWrapper">
                  <span className="headingTitle">予約一覧</span>
                  <div className="dateFilter">
                    <OtaReservationDatePicker
                      date={filterOptions.tour_date}
                      onChange={handleTourDateChange}
                    />
                    <Button
                      small={true}
                      handleClick={() => handleTourDateChange('')}
                      disabled={!filterOptions.tour_date}
                    >
                      日付フィルタをクリア
                    </Button>
                  </div>
                </div>
                <div className="headingActions">
                  <div className="search">
                    <OtaReservationSearch
                      searchOtaReservations={searchOtaReservations}
                      filterOptions={filterOptions}
                      setFilterOptions={setFilterOptions}
                      defaultFilterOptions={defaultFilterOptions}
                    />
                  </div>
                </div>
              </div>
            </Heading>
          }
          model={'ota_reservations'}
          main={
            <EditTableWrapper>
              <EditTable
                editable={false}
                columns={columns}
                records={otaReservations}
                pagination={pagination}
                getEditLink={({ id }) => `/agent/ota_reservations/${id}`}
                loading={isLoading}
                getResourceByPage={searchOtaReservations}
                isCustomResource={true}
                getRowClassName={record =>
                  record.booking_status_id === 'canceled' ? 'isCanceled' : ''
                }
              />
            </EditTableWrapper>
          }
        />
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .Index_Main {
    max-width: none !important;
    padding: 0 !important;

    .Panel {
      padding: 20px 40px !important;
      overflow: visible;

      .Panel_Title,
      .Panel_Body {
        max-width: none;
      }
    }

    .Table_Data {
      height: 48px;
      padding: 0 8px 0 10px !important;
      a {
        color: ${constants.COLORS.textLink};
        text-decoration: none;
        font-weight: 500;
        &:hover,
        &:focus {
          text-decoration: underline;
          text-underline-offset: 2px;
        }
      }
    }

    .Table_Header {
      white-space: nowrap;
      padding: 12px 8px 12px 10px !important;

      .sortableHeader {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        cursor: pointer;

        .sortIcon {
          width: 20px;
          height: 20px;
          filter: grayscale(1);
          opacity: 0.4;
          transition: opacity 0.2s;
        }

        &:hover .sortIcon {
          opacity: 0.7;
        }

        &.isActive .sortIcon {
          filter: none;
          opacity: 1;
        }
      }
    }

    .Table_Header,
    .Table_Data {
      max-width: 500px !important;
      min-width: 50px !important;
    }

    .Table_Header.tourDateColumn,
    .Table_Data.tourDateColumn {
      min-width: 190px !important;
      white-space: nowrap;
    }

    .Table_Header.noWrap,
    .Table_Data.noWrap {
      white-space: nowrap;
      word-break: normal;
    }

    .Table_Row.isCanceled {
      background-color: #f1f1f1;
      opacity: 0.65;

      .Table_Data,
      .Table_Data a {
        color: ${constants.COLORS.subText} !important;
      }
    }

    .statusFlag {
      &.isCheckedIn,
      &.isConnected {
        color: ${constants.COLORS.success};
      }

      &.notCheckedIn,
      &.notConnected {
        color: ${constants.COLORS.white};
      }

      &.isNoShow {
        color: ${constants.COLORS.danger};
      }

      .icon {
        width: 32px;
        height: 32px;
      }
    }

    .connectionTodos {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
`

const Heading = styled.div`
  .headingInner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    container-type: inline-size;
    container-name: filter-container;
    align-items: center;
    gap: 16px;
  }
  .headingTitleWrapper {
    display: flex;
    align-items: center;
    gap: 32px;
  }
  .dateFilter {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .headingTitle {
    justify-self: start;
  }
  .headingActions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-self: end;
    gap: 12px 16px;
  }
  .search {
    .filterContainer {
      margin: 0;
      color: ${constants.COLORS.subText};
      font-size: 12px;
      font-weight: normal;
      width: 400px;
    }
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    .headingInner {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    .headingActions {
      justify-self: auto;
    }
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    .headingTitle,
    .headingActions,
    .dateFilter {
      flex: 1 0 100%;
    }

    .headingTitle {
      text-align: center;
    }

    .headingActions,
    .dateFilter {
      justify-content: center;
    }

    .search {
      width: 100%;

      .filterContainer {
        width: 100%;
      }
    }
  }
`

const EditTableWrapper = styled.div`
  .Table_Row .Table_Header:first-child,
  .Table_Row .Table_Data:first-child {
    min-width: 40px;
  }
  .tourDate {
    white-space: nowrap;
  }
`

export default AgentOtaReservationIndex
